import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, PackageCheck } from "lucide-react";

import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { workshopService } from "@/services/workshop.service";
import { getErrorMessage } from "@/services/api";
import { WORKSHOP_GENERAL_CONDITIONS } from "@/config/constants";

function Field({ label, children, required, hint }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-medium">
                {label} {required && <span className="text-danger">*</span>}
            </Label>
            {children}
            {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
        </div>
    );
}

export function ReceiveEquipmentDialog({ open, onOpenChange, workshopId, onReceived }) {
    const qc = useQueryClient();
    const nowIso = new Date().toISOString().slice(0, 16);
    const { register, handleSubmit, setValue, watch, reset } = useForm({
        defaultValues: {
            arrival_datetime: nowIso,
            general_condition: "Boa",
            initial_observations: "",
        },
    });

    const mutation = useMutation({
        mutationFn: (data) => workshopService.receive(workshopId, {
            arrival_date: data.arrival_datetime
                ? new Date(data.arrival_datetime).toISOString()
                : new Date().toISOString(),
            general_condition: data.general_condition || null,
            initial_observations: data.initial_observations || null,
        }),
        onSuccess: (updated) => {
            toast.success("Equipamento recebido.");
            qc.invalidateQueries({ queryKey: ["workshop", workshopId] });
            qc.invalidateQueries({ queryKey: ["workshops"] });
            qc.invalidateQueries({ queryKey: ["workshop-dashboard"] });
            onReceived?.(updated);
            onOpenChange(false);
            reset();
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao receber equipamento.")),
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg" data-testid="receive-equipment-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <PackageCheck className="h-4 w-4 text-secondary" />
                        Receber equipamento
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Registre a chegada do equipamento na oficina. Este passo altera o status para “Recebido”.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit(mutation.mutate)} className="space-y-3">
                    <Field label="Data e hora da chegada" required>
                        <Input
                            type="datetime-local"
                            {...register("arrival_datetime")}
                            data-testid="receive-arrival-date"
                        />
                    </Field>
                    <Field label="Condição geral" hint="Como o equipamento chegou até a oficina?">
                        <Select
                            value={watch("general_condition") || ""}
                            onValueChange={(v) => setValue("general_condition", v)}
                        >
                            <SelectTrigger data-testid="receive-general-condition">
                                <SelectValue placeholder="Selecionar" />
                            </SelectTrigger>
                            <SelectContent>
                                {WORKSHOP_GENERAL_CONDITIONS.map((c) => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Observações iniciais">
                        <Textarea
                            rows={3}
                            {...register("initial_observations")}
                            placeholder="Ex: chegou embalado em plastico bolha, faltando 2 tampas..."
                            data-testid="receive-initial-observations"
                        />
                    </Field>

                    <DialogFooter className="gap-2">
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className="bg-secondary text-secondary-foreground"
                            disabled={mutation.isPending}
                            data-testid="receive-confirm-button"
                        >
                            {mutation.isPending
                                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                : <PackageCheck className="mr-2 h-4 w-4" />}
                            Confirmar recebimento
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default ReceiveEquipmentDialog;
